import commonGame from '../commonGame';
import common from '../common';
import PaletteButton from './PaletteButton';
import OutlineFilter from '../shaders/OutlineFilter';

const _createLeftBar = Symbol('_createLeftBar');
const _createRightBar = Symbol('_createRightBar');
const _onMenuButton_Click = Symbol('_onMenuButton_Click');
const _onUndoButton_Click = Symbol('_onUndoButton_Click');
const _onDrawMode_Changed = Symbol('_onDrawMode_Changed');
const _onUndoStack_Empty = Symbol('_onUndoStack_Empty');
const _onUndoStack_Increased = Symbol('_onUndoStack_Increased');
const _onPaletteBtn_Click = Symbol('_onPaletteBtn_Click');
const _onPaletteSelection_Changed = Symbol('_onPaletteSelection_Changed');
const _highlightToolBtn = Symbol('_highlightToolBtn');

export default class HUD extends PIXI.Container{
	constructor() {
		super();
		// Attributes.
		this._leftBarContainer = null;
		this._rightBarContainer = null;
		this._leftBarSprite = null;
		this._rightBarSprite = null;
		this._menuBtn = null;
		this._undoBtn = null;
		this._lastToolBtn = null;

		this._pencilButton = null;
		this._brushButton = null;
		this._bucketButton = null;
		this._stampButton = null;
		this._inkPotButton = null;
		this._sprayButton = null;
		this._eraserButton = null;
		this._paletteButton = null;

		this._drawItText = null;
		this._palettePanel = null;

		this._drawMode = commonGame.TOOL.PENCIL;

		// Signals.
		this.signals = {};
		this.signals.onMenuClick = new P3.utils.MiniSignal();
		this.signals.onPaletteClick = new P3.utils.MiniSignal();
		this.signals.onDrawModeChanged = new P3.utils.MiniSignal();

		this._tweens = new P3.utils.TWEEN.Group();
	}

	init() {
		console.assert(common.game.scene.palettePanel != null, 'Initialization failed: common.game.scene.palettePanel is null.');
		this._palettePanel = common.game.scene.palettePanel;

		const panelConfig = common.levelConfig.game_scene.panels;

		// Left bar.
		// Container.
		this._leftBarContainer = new PIXI.Container();
		this._leftBarContainer.anchor = new PIXI.Point(0, 0.5);
		this._leftBarContainer.x = (common.STAGE_WIDTH - P3.View.width) * 0.5 - this.x;
		this.addChild(this._leftBarContainer);
		// Sprite.
		this._leftBarSprite = new PIXI.Sprite(P3.assets.texture(panelConfig.left));
		this._leftBarSprite.anchor = new PIXI.Point(0, 0.5);
		this._leftBarContainer.addChild(this._leftBarSprite);

		// Right bar.
		// Container.
		this._rightBarContainer = new PIXI.Container();
		this._rightBarContainer.anchor = new PIXI.Point(0, 0.5);
		this._rightBarContainer.x = (common.STAGE_WIDTH + P3.View.width) * 0.5 - this.x;
		this.addChild(this._rightBarContainer);
		// Sprite.
		this._rightBarSprite = new PIXI.Sprite(P3.assets.texture(panelConfig.right));
		this._rightBarSprite.anchor = new PIXI.Point(1, 0.5);
		this._rightBarContainer.addChild(this._rightBarSprite);

		//--buttons--//
		this[_createLeftBar]();
		this[_createRightBar]();

		// Initial state.
		this[_highlightToolBtn](this._pencilButton);

		// Signals.
		common.game.undoManager.signals.onStackEmpty.add(this[_onUndoStack_Empty], this);
		common.game.undoManager.signals.onStackIncreased.add(this[_onUndoStack_Increased], this);

		this._palettePanel.signals.onColourChanged.add(this[_onPaletteSelection_Changed], this);
		this._palettePanel.signals.onStampChanged.add(this[_onPaletteSelection_Changed], this);
	}

	resize() {
		this._leftBarContainer.x = (common.STAGE_WIDTH - P3.View.width) * 0.5 - this.x;
		this._rightBarContainer.x = (common.STAGE_WIDTH + P3.View.width) * 0.5 - this.x;
	}

	destroy(options) {
		this._tweens.removeAll();
		super.destroy();
	}

	update() {
		this._tweens.update();
	}

	getPaletteBtnPosition(){
		return common.findPositionInScene(this._paletteButton, common.game.scene);
	}

	getBrushBtnPosition(){
		return common.findPositionInScene(this._brushButton, common.game.scene);
	}

	[_highlightToolBtn](btn){
		if (this._lastToolBtn) {
			// Remove outline.
			this._lastToolBtn.filters = [];

			this._lastToolBtn._scaleTween.stop();
			this._lastToolBtn.animate = true;
			new P3.utils.TWEEN.Tween(this._lastToolBtn.scale, this.tweens)
			    .to({
					x: this._lastToolBtn.scale.x < 0 ? -1 : 1,
					y: 1,
			    }, 250)
			    .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
			    .onStart(()=>{
			        this._lastToolBtn.interactive = true;
			    })
			    .start();
		}

		btn.animate = false;
		//btn.interactive = false;
		btn._scaleTween = new P3.utils.TWEEN.Tween(btn.scale, this.tweens)
		    .to({
		        x: 1.1 * ( btn.scale.x < 0 ? -1 : 1 ),
				y: 1.1
		    }, 500)
		    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
		    .repeat(Infinity)
		    .yoyo(true)
		    .start();

		this._lastToolBtn = btn;

		// Add outline.
		this._lastToolBtn.filters = [new OutlineFilter(9, 0xFFFFFF)];
	}

	[_createLeftBar](){
		//--buttons--//
		const buttonOffset = 90;//made an offset variable to use to offset the buttons

		// Menu button.
		this._menuBtn = createButton('menu');
		this._menuBtn.anchor = new PIXI.Point(0.5, 0.5);
		this._menuBtn.y = -280;
		this._menuBtn.x = buttonOffset;
		this._menuBtn.overSoundName = 'sfx_ui_rollover_00';
		this._menuBtn.downSoundName = 'sfx_ui_click_00';
		this._menuBtn.signals.click.add(this[_onMenuButton_Click], this);
		this._leftBarSprite.addChild(this._menuBtn);

		// Undo button.
		let btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_undo'),
			P3.assets.texture('icon_undo'),
			P3.assets.texture('icon_undo')
		);
		this._undoBtn = new P3.Button(btnStatesAux);
		this._undoBtn.anchor = new PIXI.Point(0.5, 0.5);
		this._undoBtn.y = -280;
		this._undoBtn.x = -buttonOffset;
		this._undoBtn.signals.click.add(this[_onUndoButton_Click], this);
		this._rightBarSprite.addChild(this._undoBtn);

		this._undoBtn.interactive = false;
		this._undoBtn.alpha = 0.5;

		// Tool buttons.
		// Left.
		// Palette.
		this._paletteButton = new PaletteButton();
		this._paletteButton.init();
		this._paletteButton.anchor = new PIXI.Point(0.5, 0.5);
		this._paletteButton.y = 260;
		this._paletteButton.x = buttonOffset;
		this._paletteButton.scale.x = -1;
		this._paletteButton.signals.onClick.add(this[_onPaletteBtn_Click], this);
		this._leftBarSprite.addChild(this._paletteButton);
		//this._arrToolBtn.push( this._colourPaletteButton );
		this._paletteButton.setup(common.gameConfig.palettePanel.pages[0][0], commonGame.TOOL.PENCIL);

		// Pencil.
		btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_pencil'),
			P3.assets.texture('icon_pencil'),
			P3.assets.texture('icon_pencil')
		);
		this._pencilButton = new P3.Button(btnStatesAux);
		this._pencilButton.anchor = new PIXI.Point(0.5, 0.5);
		this._pencilButton.y = 110;
		this._pencilButton.x = buttonOffset;
		this._pencilButton.drawMode = commonGame.TOOL.PENCIL;
		this._pencilButton.signals.click.add(this[_onDrawMode_Changed], this);
		this._leftBarSprite.addChild(this._pencilButton);

		// Brush.
		btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_brush'),
			P3.assets.texture('icon_brush'),
			P3.assets.texture('icon_brush'));
		this._brushButton = new P3.Button(btnStatesAux);
		this._brushButton.anchor = new PIXI.Point(0.5, 0.5);
		this._brushButton.y = -10;
		this._brushButton.x = buttonOffset;
		this._brushButton.drawMode = commonGame.TOOL.BRUSH;
		this._brushButton.signals.click.add(this[_onDrawMode_Changed], this);
		this._leftBarSprite.addChild(this._brushButton);

		// Bucket.
		btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_fill'),
			P3.assets.texture('icon_fill'),
			P3.assets.texture('icon_fill'));
		this._bucketButton = new P3.Button(btnStatesAux);
		this._bucketButton.anchor = new PIXI.Point(0.5, 0.5);
		this._bucketButton.y = -130;
		this._bucketButton.x = buttonOffset;
		this._bucketButton.drawMode = commonGame.TOOL.BUCKET;
		this._bucketButton.signals.click.add(this[_onDrawMode_Changed], this);
		this._leftBarSprite.addChild(this._bucketButton);
	}

	[_createRightBar](){
		const buttonOffset = 90; // made an offset variable to use to offset the buttons

		// Stamp.
		let btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_stamp'),
			P3.assets.texture('icon_stamp'),
			P3.assets.texture('icon_stamp')
		);
		this._stampButton = new P3.Button(btnStatesAux);
		this._stampButton.anchor = new PIXI.Point(0.5, 0.5);
		this._stampButton.scale.x = -1;
		//this._stampButton.defaultScale.x = this._stampButton.scale.x;
		this._stampButton.y = 230;
		this._stampButton.x = -buttonOffset;
		this._stampButton.drawMode = commonGame.TOOL.STAMP;
		this._stampButton.signals.click.add(this[_onDrawMode_Changed], this);
		this._rightBarSprite.addChild(this._stampButton);

		// Ink pot.
		btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_ink'),
			P3.assets.texture('icon_ink'),
			P3.assets.texture('icon_ink')
		);
		this._inkPotButton = new P3.Button(btnStatesAux);
		this._inkPotButton.anchor = new PIXI.Point(0.5, 0.5);
		this._inkPotButton.y = 110;
		this._inkPotButton.x = -buttonOffset;
		this._inkPotButton.drawMode = commonGame.TOOL.INK_POT;
		this._inkPotButton.signals.click.add(this[_onDrawMode_Changed], this);
		this._rightBarSprite.addChild(this._inkPotButton);

		// Spray.
		btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_spray'),
			P3.assets.texture('icon_spray'),
			P3.assets.texture('icon_spray')
		);
		this._sprayButton = new P3.Button(btnStatesAux);
		this._sprayButton.anchor = new PIXI.Point(0.5, 0.5);
		this._sprayButton.y = -10;
		this._sprayButton.x = -buttonOffset;
		this._sprayButton.drawMode = commonGame.TOOL.SPRAY;
		this._sprayButton.signals.click.add(this[_onDrawMode_Changed], this);
		this._rightBarSprite.addChild(this._sprayButton);

		// Eraser.
		btnStatesAux = new P3.ButtonStates(
			P3.assets.texture('icon_eraser'),
			P3.assets.texture('icon_eraser'),
			P3.assets.texture('icon_eraser')
		);
		this._eraserButton = new P3.Button(btnStatesAux);
		this._eraserButton.anchor = new PIXI.Point(0.5, 0.5);
		this._eraserButton.y = -130;
		this._eraserButton.x = -buttonOffset;
		this._eraserButton.drawMode = commonGame.TOOL.ERASER;
		this._eraserButton.signals.click.add(this[_onDrawMode_Changed], this);
		this._rightBarSprite.addChild(this._eraserButton);
	}

	[_onMenuButton_Click](){
		this.signals.onMenuClick.dispatch();
	}

	[_onUndoButton_Click](){
		common.game.undoManager.undo();


		// Sfx.
		P3.audio.playSound('sfx_tool_undo_step_back_00');
	}

	[_onDrawMode_Changed](btn){
		// Highlight selected button.
		this[_highlightToolBtn](btn);

		this._drawMode = btn.drawMode;

		this.signals.onDrawModeChanged.dispatch(btn.drawMode);

		// Palette button.
		if (btn.drawMode === commonGame.TOOL.ERASER)
		// Disable it if the eraser is selected.
			this._paletteButton.setInteractive(false);
		else {
			// Otherwise, enable it.
			this._paletteButton.setInteractive(true);

			// Update visual elements.
			if (btn.drawMode === commonGame.TOOL.BUCKET)
				this._paletteButton.setup(common.game.canvas.fillLayer.paletteItem, btn.drawMode);
			else
				this._paletteButton.setup(common.game.canvas.drawLayer.drawingTool.paletteItem, btn.drawMode);
		}


		// Tutorial.
		common.game.scene.addTutorialEvent(commonGame.TUT_EVENT.TOOL_SELECTED);
		if (btn.drawMode !== commonGame.TOOL.ERASER)
			common.game.scene.addTutorialEvent(commonGame.TUT_EVENT.PALETTE_BUTTON_ENABLED);
		else
			common.game.scene.addTutorialEvent(commonGame.TUT_EVENT.PALETTE_BUTTON_DISABLED);
	}

	[_onUndoStack_Empty](){
		this._undoBtn.interactive = false;
		this._undoBtn.alpha = 0.5;
	}

	[_onUndoStack_Increased](){
		this._undoBtn.interactive = true;
		this._undoBtn.alpha = 1;
	}

	[_onPaletteBtn_Click](){
		console.log('palette click');
		this.signals.onPaletteClick.dispatch(this._paletteButton.type);
	}

	[_onPaletteSelection_Changed](selection){
		this._paletteButton.setup(selection, this._paletteButton.type);
	}

	get tweens(){
		return this._tweens;
	}
}